import React, { useEffect } from 'react';
import { Alert, Form, FormInstance } from 'antd';
import { Card } from '../../Card';
import { Heading } from '../../Heading';
import { useTranslation } from 'react-i18next';
// import SwitchWalletCard from './SwitchWalletCard';
// import DiscountInputCard from './DiscountInputCard';
import OrderShipmentValueCard from './OrderShipmentValueCard/OrderShipmentValueCard';
import { PaymentType, Preview, ShipmentFeeType } from '@/types';
import CustomShipmentFeeCard from './CustomShipmentFeeCard';

interface PaymentCardProps {
  form: FormInstance;
  orderPrice: Preview;
  orderPriceLoading: boolean;
  paymentType?: PaymentType;
  shipmentFeeType?: ShipmentFeeType;
  onCustomShipmentFeeChange: (shipmentFee?: number) => void;
  customShipmentFee?: number;
  discountAmount?: number;
  prepaymentAmount?: number;
  /**
   * Why this order cannot be priced, when the backend gave a definitive answer.
   *
   * Without it the summary below simply shows 0 IQD, and a zero reads as a free
   * shipment rather than as a refusal — reported live 2026-08-26 on a Basra
   * province rule pointing at Boxy Save with temperature control on.
   */
  blockedReason?: string | null;
}

export const PaymentCard: React.FC<PaymentCardProps> = ({
  form,
  orderPrice,
  orderPriceLoading,
  paymentType,
  shipmentFeeType,
  onCustomShipmentFeeChange,
  customShipmentFee,
  discountAmount,
  prepaymentAmount,
  blockedReason,
}) => {
  const { t } = useTranslation();
  // const [discountCode, setDiscountCode] = useState('');

  // Read the selection straight off the form instead of mirroring it into local
  // state. A mirror only re-synced when the props changed, so anything that set
  // the field programmatically — Smart Paste filling the form, back-nav restore —
  // moved the value but left these buttons showing the old choice.
  const watchedPaymentType = Form.useWatch('CodOrPrepaid', form);
  const watchedShipmentFeeType = Form.useWatch('CustomerOrMerchant', form);

  const selectedPaymentTypeValue =
    watchedPaymentType ?? paymentType ?? PaymentType.COLLECT_ON_DELIVERY;
  const selectedShipmentFeeTypeValue =
    watchedShipmentFeeType ?? shipmentFeeType ?? ShipmentFeeType.BY_MERCHANT;

  useEffect(() => {
    const initialPaymentType = paymentType || form.getFieldValue('CodOrPrepaid'); // Use packageSizeProp if available
    const initialShipmentFeeType = shipmentFeeType || form.getFieldValue('CustomerOrMerchant'); // Use packageSizeProp if available

    if (initialPaymentType && initialShipmentFeeType) {
      form.setFieldsValue({
        CodOrPrepaid: initialPaymentType,
        CustomerOrMerchant: initialShipmentFeeType,
      });
    }
  }, [paymentType, shipmentFeeType, form]);

  // const handleRadioChangeShipmentFeeType = (e: RadioChangeEvent) => {
  //   setSelectedShipmentFeeTypeValue(e.target.value);
  // };

  return (
    <Card>
      <Heading
        title={t('payment')}
        fontSize='text-xs'
        fontWeight='font-bold'
        color='text-sd-muted-foreground'
        extraClassName='uppercase tracking-wider mb-4'
      />
      <Form.Item name='CodOrPrepaid' noStyle>
        <input type='hidden' />
      </Form.Item>
      <div className='flex gap-2 mb-4'>
        {[
          { value: PaymentType.COLLECT_ON_DELIVERY, label: t('cod') },
          { value: PaymentType.PREPAID, label: t('prepaid') },
        ].map((option) => {
          const isSelected = selectedPaymentTypeValue === option.value;
          return (
            <button
              key={option.value}
              type='button'
              onClick={() => {
                form.setFieldValue('CodOrPrepaid', option.value);
              }}
              className={`flex-1 rounded-lg border border-solid px-3 py-2.5 text-xs font-semibold uppercase transition-colors cursor-pointer ${
                isSelected
                  ? 'border-sd-primary bg-sd-primary/20 text-sd-foreground'
                  : 'border-sd-input bg-sd-card text-sd-muted-foreground hover:border-sd-primary/50'
              }`}
            >
              {String(option.label)}
            </button>
          );
        })}
      </div>
      <Form.Item name='CustomerOrMerchant' initialValue={shipmentFeeType} noStyle>
        <input type='hidden' />
      </Form.Item>

      {/* <Flex vertical className='mt-4'>
        <Text className='font-normal mb-2 text-xs leading-[10px] text-customGrayShade5 capitalize'>
          {t('shipment_fee')}
        </Text>
        <Form.Item name='CustomerOrMerchant' initialValue={shipmentFeeType}>
          <Radio.Group
            value={selectedShipmentFeeTypeValue}
            onChange={handleRadioChangeShipmentFeeType}
            className='w-full'
          >
            <Row gutter={[24, 16]} className='mt-[19.5px]'>
              <Col lg={12} md={24} xs={24}>
                <CustomRadioButton
                  value={ShipmentFeeType.BY_CUSTOMER}
                  selectedValue={selectedShipmentFeeTypeValue}
                  label={t('customer')}
                />
              </Col>
              <Col lg={12} md={24} xs={24}>
                <CustomRadioButton
                  value={ShipmentFeeType.BY_MERCHANT}
                  selectedValue={selectedShipmentFeeTypeValue}
                  label={t('merchant')}
                />
              </Col>
            </Row>
          </Radio.Group>
        </Form.Item>
      </Flex> */}
      {/* {selectedShipmentFeeTypeValue === ShipmentFeeType.BY_CUSTOMER && (
        <CustomShipmentFeeCard
          onCustomShipmentFeeChange={onCustomShipmentFeeChange}
          customShipmentFee={customShipmentFee}
        />
      )} */}
      {/* V0 remove */}
      {/* <SwitchWalletCard
        availableIqd={1}
        walletBalanceText={t('wallet_balance')}
        availableText={t('available')}
        iqdText={t('iqd')}
      />
      <DiscountInputCard
        placeholder={t('discount_code')}
        value={discountCode}
        onChange={setDiscountCode}
      /> */}
      {blockedReason ? (
        <Alert
          type='warning'
          showIcon
          className='mb-3'
          message={blockedReason}
          data-testid='preview-blocked-reason'
        />
      ) : null}
      <OrderShipmentValueCard
        orderValue={orderPrice.orders_value}
        shipmentFee={orderPrice.orders_shipment_fee || 0}
        customShipmentFee={orderPrice.orders_custom_shipment_fee || 0}
        customShipmentFeeText={t('customer_shipping_fee')}
        shipmentFeeType={selectedShipmentFeeTypeValue}
        paymentType={selectedPaymentTypeValue}
        total={orderPrice.orders_value - (orderPrice.orders_shipment_fee || 0)}
        orderPriceLoading={orderPriceLoading}
        orderValueText={t('order_value')}
        shipmentFeeText={t('shipment_fee')}
        totalText={t('total')}
        currency={t('iqd')}
        discountAmount={discountAmount ?? 0}
        prepaymentAmount={prepaymentAmount ?? 0}
      />
    </Card>
  );
};
